import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { useEmployeeRates } from "@/hooks/use-employee-rates";

export interface EmployeeRow {
  id: string;
  full_name: string;
  email: string;
  role: string;
  avatar_url: string | null;
  // hourly_cost_rate and hourly_sales_rate are fetched separately via useEmployeeRates (RPC)
  // after column-level REVOKE in migration 20260601160500
  hourly_cost_rate: number | null;
  hourly_sales_rate: number | null;
  hire_date: string | null;
  monthly_capacity_hours: number | null;
  is_active: boolean;
  project_count: number;
  total_budgeted_hours: number;
  [key: string]: unknown; // allow extra DB columns
}

/**
 * Non-hook data fetch for active employees with aggregated project/budget data.
 * Extracted so it can be reused outside React Query (e.g. report resolvers).
 * Sensitive rate columns are NOT included here — they come from useEmployeeRates().
 */
export async function fetchEmployees(): Promise<EmployeeRow[]> {
  // Fetch users — omit revoked columns; they are merged from useEmployeeRates
  const { data: users, error } = await supabase
    .from("users")
    .select(
      "id, full_name, email, role, avatar_url, monthly_capacity_hours, is_active, " +
      "specialization, job_title, phone, target_billing_rate, birthday, created_at"
    )
    .eq("is_active", true)
    .order("full_name");
  if (error) throw error;

  // Fetch budget assignments per user (total budgeted hours + which projects).
  // budget_lines(project_id) lets us count assignment-only projects, not just
  // projects the user owns (VIB: project_count was owner-only → users with
  // assigned hours but no ownership showed 0 projects).
  // TEAM-WIDE unbounded query (no project/user filter) — page past the
  // 1000-row cap so aggregation reflects every assignment (see VIB-946).
  const assignments = await fetchAllPaginated<{
    user_id: string;
    hours: number | null;
    budget_lines?: unknown;
  }>("budget_line_assignments", "user_id, hours, budget_lines(project_id)");

  // Fetch project ownership counts per user
  const { data: ownerships } = await supabase
    .from("project_owners")
    .select("user_id, project_id");

  // Aggregate. project_count = distinct projects from ownerships ∪ assignments.
  const hoursMap = new Map<string, number>();
  const projectSet = new Map<string, Set<string>>();

  const addProject = (userId: string, projectId: string | null | undefined) => {
    if (!projectId) return;
    if (!projectSet.has(userId)) projectSet.set(userId, new Set());
    projectSet.get(userId)!.add(projectId);
  };

  for (const a of assignments ?? []) {
    hoursMap.set(a.user_id, (hoursMap.get(a.user_id) ?? 0) + (a.hours ?? 0));
    // PostgREST returns the to-one embed as an object, but tolerate an array too.
    const bl = (a as { budget_lines?: unknown }).budget_lines;
    const projectId = Array.isArray(bl)
      ? bl[0]?.project_id
      : (bl as { project_id?: string } | null)?.project_id;
    addProject(a.user_id, projectId);
  }
  for (const o of ownerships ?? []) {
    addProject(o.user_id, o.project_id);
  }

  return (users ?? []).map((u: any) => ({
    ...u,
    // Rates placeholders — merged with ratesQuery data in useEmployees return value
    hourly_cost_rate: null,
    hourly_sales_rate: null,
    hire_date: null,
    project_count: projectSet.get(u.id)?.size ?? 0,
    total_budgeted_hours: hoursMap.get(u.id) ?? 0,
  }));
}

/**
 * Fetches all active users with aggregated project/budget data.
 * Sensitive columns (hourly_cost_rate, hourly_sales_rate, hire_date) are
 * fetched separately via useEmployeeRates() (RPC) after column-level REVOKE.
 */
export function useEmployees() {
  const ratesQuery = useEmployeeRates();

  const usersQuery = useQuery({
    queryKey: ["employees"],
    queryFn: fetchEmployees,
    staleTime: 2 * 60 * 1000,
  });

  // Merge rate data from RPC into employee rows when both queries are ready
  const data = usersQuery.data?.map((emp) => {
    const rates = ratesQuery.data?.get(emp.id);
    if (!rates) return emp;
    return {
      ...emp,
      hourly_cost_rate: rates.hourly_cost_rate,
      hourly_sales_rate: rates.hourly_sales_rate,
      hire_date: rates.hire_date,
    };
  });

  // Listen styres KUN av usersQuery. Rater er supplerende (admin-visning) og
  // merges inn når de er klare; en rate-feil skal aldri tomme/feile hele listen
  // (ellers forsvinner alle employee-rader hvis RPC-en mangler/feiler).
  return {
    ...usersQuery,
    data,
  };
}
